import React from "react";
import { NavLink } from "react-router-dom";
import {
  Home,
  BarChart3,
  Package,
  Target,
  Search,
  Globe,
  Instagram,
  Link,
  Moon,
  Sun,
  LogOut,
  MonitorSpeaker,
  Percent,
  Users,
  FileText,
  TrendingUp,
  MapPin,
  UserCog,
  Building2,
  UsersRound,
  ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { ProfileResponse } from "@/services/user.service";
import { useAccount } from "@/contexts/account.context";
import { useAuth } from "@/contexts/auth.context";
import { ehAssinante } from "@/lib/account-access-context";

interface DrawerMobileProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogout: () => void;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  user: ProfileResponse | null;
  isAgencyUser?: boolean;
}

/**
 * Menu items matching the desktop AppSidebar exactly.
 * Grouped by section for visual parity.
 */
const menuGroups = [
  {
    title: "Dashboard",
    items: [{ path: "/dashboard", icon: Home, label: "Visão Geral" }],
  },
  {
    title: "Performance de Mídia",
    items: [
      { path: "/dashboard/products", icon: Package, label: "Produtos" },
      {
        path: "/dashboard/conversion-rate",
        icon: Percent,
        label: "Taxa de Conversão",
      },
      { path: "/dashboard/acquisition", icon: Users, label: "Aquisição" },
      { path: "/dashboard/ads", icon: TrendingUp, label: "ADS" },
      { path: "/dashboard/utm", icon: Link, label: "UTM" },
      { path: "/dashboard/pages", icon: FileText, label: "Páginas" },
      { path: "/dashboard/receita", icon: TrendingUp, label: "Receita" },
      { path: "/dashboard/regiao", icon: MapPin, label: "Região" },
      { path: "/dashboard/seo", icon: Search, label: "SEO" },
      { path: "/dashboard/instagram", icon: Instagram, label: "Instagram" },
      { path: "/dashboard/historico", icon: FileText, label: "Histórico" },
      {
        path: "/dashboard/media-performance",
        icon: MonitorSpeaker,
        label: "Performance Geral",
      },
      {
        path: "/dashboard/google-analytics",
        icon: BarChart3,
        label: "Google Analytics",
      },
      { path: "/dashboard/meta-ads", icon: Target, label: "Meta Ads" },
      { path: "/dashboard/google-ads", icon: Search, label: "Google Ads" },
      {
        path: "/dashboard/search-console",
        icon: Globe,
        label: "Search Console",
      },
    ],
  },
  {
    title: "Configurações",
    items: [
      { path: "/dashboard/my-account", icon: UserCog, label: "Minha Conta" },
      {
        path: "/dashboard/accounts",
        icon: Building2,
        label: "Contas da Agência",
      },
      {
        path: "/dashboard/teams",
        icon: UsersRound,
        label: "Times",
        teamsOnly: true,
      },
      { path: "/dashboard/integrations", icon: Link, label: "Integrações" },
    ],
  },
];

export const DrawerMobile: React.FC<DrawerMobileProps> = ({
  open,
  onOpenChange,
  onLogout,
  darkMode,
  onToggleDarkMode,
  user,
  isAgencyUser = false,
}) => {
  const { canManageTeams, isTeamMember } = useAuth();
  const { isTeamScoped, isReadOnly } = useAccount();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] lg:hidden">
        <SheetHeader className="border-b pb-3">
          <SheetTitle className="text-left text-base">Menu</SheetTitle>
        </SheetHeader>

        <div
          className="overflow-y-auto flex-1 py-3"
          style={{ maxHeight: "calc(85vh - 180px)" }}
        >
          {menuGroups.map((group) => (
            <div key={group.title} className="mb-3">
              <p className="px-4 py-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                {group.title}
              </p>
              <div className="space-y-0.5">
                {group.items
                  // Mesma regra da sidebar. <M-279> A gestão de contas é de todo dono de
                  // conta — o assinante —, tenha ele uma loja ou várias. <M-248> As mesmas duas perguntas: a
                  // gestão de contas é da agência (`403` para quem navega por escopo cedido, seja
                  // qual for o nível), e conectar integração é escrita na loja aberta.
                  .filter(
                    (item) =>
                      item.path !== "/dashboard/accounts" ||
                      (!isTeamScoped && ehAssinante(user)),
                  )
                  .filter(
                    (item) =>
                      !isReadOnly || item.path !== "/dashboard/integrations",
                  )
                  // Times: quem administra a agência gere; quem é membro consulta os seus.
                  .filter(
                    (item) =>
                      !("teamsOnly" in item && item.teamsOnly) ||
                      canManageTeams ||
                      isTeamMember,
                  )
                  .map((item) => {
                    const Icon = item.icon;
                    // Mesma regra da sidebar: o rótulo de agência só vale para agência, e
                    // "Times" vira "Meus times" para quem não os administra.
                    const label =
                      item.path === "/dashboard/accounts" && !isAgencyUser
                        ? "Minhas Contas"
                        : item.path === "/dashboard/teams" && !canManageTeams
                          ? "Meus times"
                          : item.label;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === "/dashboard"}
                        onClick={() => onOpenChange(false)}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center px-4 py-2.5 rounded-lg transition-colors mx-2",
                            isActive
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-foreground hover:bg-muted",
                          )
                        }
                      >
                        <Icon className="h-4 w-4 mr-3 shrink-0" />
                        <span className="text-sm">{label}</span>
                      </NavLink>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>

        {/* User section */}
        <div className="border-t pt-3 mt-auto">
          {/* <M-249> Mesmo caminho da sidebar: o bloco do usuário abre "Meu Perfil". */}
          <NavLink
            to="/dashboard/account"
            onClick={() => onOpenChange(false)}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-4 py-2 mb-2 mx-2 rounded-lg transition-colors",
                isActive ? "bg-primary/10 text-primary" : "hover:bg-muted",
              )
            }
          >
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.avatar_url ?? undefined} />
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {user?.name || "Usuário"}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {user?.email}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </NavLink>

          <div className="flex gap-2 px-4 pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onToggleDarkMode();
                onOpenChange(false);
              }}
              className="flex-1 justify-center h-9 text-xs"
            >
              {darkMode ? (
                <Sun className="h-4 w-4 mr-1.5" />
              ) : (
                <Moon className="h-4 w-4 mr-1.5" />
              )}
              {darkMode ? "Modo Claro" : "Modo Escuro"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onLogout();
                onOpenChange(false);
              }}
              className="flex-1 justify-center h-9 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Sair
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
